import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  Slider,
  Chip,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { DEFAULT_HISTORY_FILTERS } from "../../history/helpers";

const formatPercentage = (value) => `${Math.round(value * 100)}%`;

function HistoryFiltersModal({ open, onClose, filters, onFiltersChange }) {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    if (open) {
      setLocalFilters(filters);
    }
  }, [open, filters]);

  const handleFilterChange = useCallback((key, value) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleApply = useCallback(() => {
    onFiltersChange(localFilters);
    onClose();
  }, [localFilters, onClose, onFiltersChange]);

  const handleReset = useCallback(() => {
    setLocalFilters(DEFAULT_HISTORY_FILTERS);
    onFiltersChange(DEFAULT_HISTORY_FILTERS);
  }, [onFiltersChange]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        {t("risksMap.history.filters.title")}
        <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label={t("common.from")}
              type="date"
              value={localFilters.creationTimeStart}
              onChange={(event) =>
                handleFilterChange("creationTimeStart", event.target.value)
              }
              size="small"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label={t("common.to")}
              type="date"
              value={localFilters.creationTimeEnd}
              onChange={(event) =>
                handleFilterChange("creationTimeEnd", event.target.value)
              }
              size="small"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>

          <Box sx={{ padding: "0px 12px" }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("portfolioMap.fireRiskRange")}
            </Typography>
            <Slider
              value={localFilters.fireRiskRange}
              onChange={(_, value) => handleFilterChange("fireRiskRange", value)}
              valueLabelDisplay="auto"
              valueLabelFormat={formatPercentage}
              min={0}
              max={1}
              step={0.01}
              marks={[
                { value: 0, label: "0%" },
                { value: 0.5, label: "50%" },
                { value: 1, label: "100%" },
              ]}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                label={`${t("common.min")}: ${formatPercentage(
                  localFilters.fireRiskRange[0],
                )}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${t("common.max")}: ${formatPercentage(
                  localFilters.fireRiskRange[1],
                )}`}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>

          <Box sx={{ padding: "0px 12px" }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("portfolioMap.floodRiskRange")}
            </Typography>
            <Slider
              value={localFilters.floodRiskRange}
              onChange={(_, value) => handleFilterChange("floodRiskRange", value)}
              valueLabelDisplay="auto"
              valueLabelFormat={formatPercentage}
              min={0}
              max={1}
              step={0.01}
              marks={[
                { value: 0, label: "0%" },
                { value: 0.5, label: "50%" },
                { value: 1, label: "100%" },
              ]}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                label={`${t("common.min")}: ${formatPercentage(
                  localFilters.floodRiskRange[0],
                )}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${t("common.max")}: ${formatPercentage(
                  localFilters.floodRiskRange[1],
                )}`}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>

          <Box sx={{ padding: "0px 12px" }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("portfolioMap.earthquakeRiskRange")}
            </Typography>
            <Slider
              value={localFilters.earthquakeRiskRange}
              onChange={(_, value) => handleFilterChange("earthquakeRiskRange", value)}
              valueLabelDisplay="auto"
              valueLabelFormat={formatPercentage}
              min={0}
              max={1}
              step={0.01}
              marks={[
                { value: 0, label: "0%" },
                { value: 0.5, label: "50%" },
                { value: 1, label: "100%" },
              ]}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                label={`${t("common.min")}: ${formatPercentage(
                  localFilters.earthquakeRiskRange[0],
                )}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${t("common.max")}: ${formatPercentage(
                  localFilters.earthquakeRiskRange[1],
                )}`}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: "12px" }}>
        <Button onClick={handleReset}>{t("common.reset")}</Button>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button onClick={handleApply} variant="contained">
          {t("portfolioMap.applyFilters")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default HistoryFiltersModal;
